<template>
  <div class="photography-packages-view min-h-screen bg-white">
    <!-- Breadcrumb -->
    <section class="bg-white border-b border-gray-100">
      <div class="max-w-6xl mx-auto px-6 lg:px-8 py-4">
        <nav class="flex items-center text-sm text-gray-500">
          <router-link to="/" class="hover:text-gray-900 transition-colors">{{ $t('nav.home') }}</router-link>
          <ChevronRight class="w-4 h-4 mx-2" />
          <router-link to="/packages" class="hover:text-gray-900 transition-colors">{{ $t('packages.title') }}</router-link>
          <ChevronRight class="w-4 h-4 mx-2" />
          <span class="text-gray-900">{{ $t('packages.photography.title') }}</span>
        </nav>
      </div>
    </section>

    <!-- Header -->
    <section class="bg-white">
      <div class="max-w-6xl mx-auto px-6 lg:px-8 pt-12 pb-8">
        <div class="text-center">
          <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{{ $t('packages.photography.subtitle') }}</p>
          <h1 class="text-2xl md:text-3xl font-light text-gray-900">{{ $t('packages.photography.title') }}</h1>
          <p class="text-gray-500 mt-4 max-w-2xl mx-auto">{{ $t('packages.photography.description') }}</p>
        </div>
      </div>
    </section>

    <!-- Packages Section -->
    <section class="py-12 bg-stone-50">
      <div class="max-w-6xl mx-auto px-6 lg:px-8">
        <!-- Filters -->
        <div class="mb-12">
          <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">{{ $t('packages.filters.refine') }}</p>
              <h2 class="text-lg font-light text-gray-900">{{ $t('packages.filters.title') }}</h2>
            </div>

            <div class="flex flex-col sm:flex-row gap-4">
              <!-- Package Type Filter -->
              <div class="sm:w-44">
                <select
                  v-model="filters.package_type"
                  @change="applyFilters"
                  class="w-full px-4 py-3 bg-white border border-gray-200 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm transition-colors appearance-none cursor-pointer"
                  style="background-image: url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 24 24%22 stroke=%22currentColor%22%3E%3Cpath stroke-linecap=%22round%22 stroke-linejoin=%22round%22 stroke-width=%222%22 d=%22M19 9l-7 7-7-7%22/%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 12px center; background-size: 16px; padding-right: 40px;"
                >
                  <option value="">{{ $t('packages.filters.all_types') }}</option>
                  <option value="premium">{{ $t('packages.filters.premium') }}</option>
                  <option value="basic">{{ $t('packages.filters.basic') }}</option>
                  <option value="full_set">{{ $t('packages.filters.full_set') }}</option>
                </select>
              </div>

              <!-- Gender Filter -->
              <div class="sm:w-44">
                <select
                  v-model="filters.gender"
                  @change="applyFilters"
                  class="w-full px-4 py-3 bg-white border border-gray-200 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm transition-colors appearance-none cursor-pointer"
                  style="background-image: url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 fill=%22none%22 viewBox=%220 0 24 24%22 stroke=%22currentColor%22%3E%3Cpath stroke-linecap=%22round%22 stroke-linejoin=%22round%22 stroke-width=%222%22 d=%22M19 9l-7 7-7-7%22/%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 12px center; background-size: 16px; padding-right: 40px;"
                >
                  <option value="">{{ $t('packages.filters.all_genders') }}</option>
                  <option value="female">{{ $t('packages.filters.female') }}</option>
                  <option value="male">{{ $t('packages.filters.male') }}</option>
                </select>
              </div>

              <!-- Reset Button -->
              <div class="sm:w-auto flex items-end">
                <button
                  @click="resetFilters"
                  class="px-6 py-3 bg-white border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="!hasActiveFilters"
                >
                  {{ $t('packages.filters.reset') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center py-20">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
        </div>

        <!-- Results Count -->
        <div v-else class="flex items-center justify-between mb-8">
          <p class="text-sm text-gray-600">
            {{ $t('packages.filters.showing') }} <span class="font-medium text-gray-900">{{ packages.length }}</span> {{ $t('packages.filters.of') }}
            <span class="font-medium text-gray-900">{{ totalCount }}</span> {{ $t('packages.filters.packages') }}
          </p>
        </div>

        <!-- Packages Grid -->
        <div v-if="!loading && packages.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-12">
          <div
            v-for="pkg in packages"
            :key="pkg.id"
            data-testid="package-card"
            :data-package-code="pkg.code"
            class="group bg-white border border-gray-100 p-6 hover:shadow-xl hover:border-gray-200 transition-all duration-500 flex flex-col"
          >
            <!-- Content Wrapper - flex-1 to push Price & Action to bottom -->
            <div class="flex-1">
              <!-- Package Header -->
              <div class="flex items-start justify-between mb-4">
                <span
                  :class="[
                    'px-2.5 py-1 text-xs font-medium uppercase tracking-wide',
                    pkg.package_type === 'premium'
                      ? 'bg-gradient-to-r from-[#60a5fa] via-[#81c4ff] to-[#60a5fa] text-white'
                      : 'bg-[#f6e7d3] text-gray-800'
                  ]"
                >
                  {{ getPackageTypeLabel(pkg.package_type) }}
                </span>
                <!-- Gender Avatar - show only if gender is not 'any' -->
                <img
                  v-if="pkg.gender !== 'any'"
                  :src="getAvatarPath(pkg.age_group, pkg.gender)"
                  :alt="`${pkg.age_group} ${pkg.gender}`"
                  class="w-8 h-8 object-cover rounded-full border border-gray-600"
                />
              </div>

              <!-- Package Name -->
              <h3 class="text-base font-medium text-gray-900 mb-2 line-clamp-2 group-hover:text-gray-700 transition-colors">{{ getLocalizedPackageName(pkg) }}</h3>

              <!-- Description -->
              <p v-if="getLocalizedPackageDescription(pkg)" class="text-sm text-gray-500 mb-3 whitespace-pre-line">{{ getLocalizedPackageDescription(pkg) }}</p>

              <!-- Services -->
              <div class="flex flex-wrap gap-2 mb-4">
                <span
                  v-for="service in pkg.services"
                  :key="service"
                  class="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs border border-blue-100"
                >
                  {{ getServiceName(service) }}
                </span>
              </div>

              <!-- Duration (for photography) - Hidden -->
              <div v-if="false" class="flex items-center gap-4 text-xs text-gray-500 mb-4">
                <span class="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ pkg.duration_minutes }} {{ $t('packages.card.min') }}
                </span>
              </div>
            </div>

            <!-- Price & Action - Always at bottom -->
            <div class="pt-4 border-t border-gray-100">
              <div class="flex items-end justify-between mb-4">
                <div>
                  <p class="text-xs text-gray-400 mb-1 uppercase tracking-wider">{{ $t('packages.card.price') }}</p>
                  <p class="text-2xl font-light text-gray-900">฿{{ formatPrice(pkg.price) }}</p>
                </div>
                <span class="text-xs text-gray-400 bg-stone-50 px-2 py-1">
                  {{ $t('packages.card.deposit') }}: ฿{{ pkg.deposit_amount.toLocaleString() }}
                </span>
              </div>
              <button
                @click="openConfirmModal(pkg)"
                data-testid="select-package"
                class="w-full inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-all duration-300"
              >
                {{ $t('packages.card.select') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && packages.length === 0" class="text-center py-20 bg-white border border-gray-100">
          <div class="w-20 h-20 mx-auto mb-6 bg-blue-50 flex items-center justify-center">
            <Camera class="h-10 w-10 text-blue-300" />
          </div>
          <p class="text-xs uppercase tracking-[0.3em] text-gray-400 mb-3">{{ $t('packages.filters.no_results') }}</p>
          <h3 class="text-xl font-light text-gray-900 mb-3">{{ $t('packages.filters.no_packages_found') }}</h3>
          <p class="text-gray-500 mb-8 text-sm max-w-md mx-auto">{{ $t('packages.filters.try_adjusting') }}</p>
          <button
            @click="resetFilters"
            class="inline-flex items-center justify-center px-10 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
          >
            {{ $t('packages.filters.clear_filters') }}
          </button>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex items-center justify-center gap-2">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {{ $t('packages.pagination.previous') }}
          </button>

          <div class="flex items-center gap-1">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="goToPage(page)"
              :class="[
                'px-4 py-2 text-sm font-medium transition-all',
                currentPage === page
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
              ]"
            >
              {{ page }}
            </button>
          </div>

          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {{ $t('packages.pagination.next') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Success Modal -->
    <TransitionRoot appear :show="showSuccessModal" as="template">
      <Dialog as="div" @close="closeSuccessModal" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel data-testid="cart-success-modal" class="w-full max-w-md bg-white overflow-hidden shadow-2xl">
                <!-- Header -->
                <div class="bg-gray-900 px-6 py-4">
                  <DialogTitle class="text-lg font-light text-white tracking-wide">
                    {{ $t('packages.modal.success_title') }}
                  </DialogTitle>
                </div>

                <!-- Content -->
                <div class="p-6">
                  <!-- Success Message -->
                  <div class="text-center mb-8">
                    <div class="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-gray-900 font-medium text-base mb-2">
                      {{ $t('packages.modal.added_to_cart') }}
                    </p>
                    <p class="text-gray-500 text-sm">
                      {{ $t('packages.modal.manage_in_cart') }}
                    </p>
                  </div>

                  <!-- Selected Package Info -->
                  <div v-if="selectedPackageForConfirm" class="bg-blue-50 p-4 mb-6 border border-blue-100">
                    <div class="flex items-center gap-2 mb-2">
                      <span
                        :class="[
                          'px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
                          selectedPackageForConfirm.package_type === 'premium'
                            ? 'bg-gradient-to-r from-[#60a5fa] via-[#81c4ff] to-[#60a5fa] text-white'
                            : 'bg-[#f6e7d3] text-gray-800'
                        ]"
                      >
                        {{ getPackageTypeLabel(selectedPackageForConfirm.package_type) }}
                      </span>
                    </div>
                    <h4 class="font-medium text-gray-900 mb-2">{{ selectedPackageForConfirm.name }}</h4>
                    <p class="text-lg font-light text-gray-900">฿{{ formatPrice(selectedPackageForConfirm.price) }}</p>
                  </div>

                  <!-- Actions -->
                  <div class="flex gap-3">
                    <button
                      @click="closeSuccessModal"
                      class="flex-1 inline-flex items-center justify-center px-6 py-3 border border-gray-200 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
                    >
                      {{ $t('packages.modal.continue_shopping') }}
                    </button>
                    <button
                      @click="goToCart"
                      data-testid="view-cart"
                      class="flex-1 inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
                    >
                      {{ $t('packages.modal.view_cart') }}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ChevronRight, Camera } from 'lucide-vue-next'
import { useCartStore } from '@/stores/cart'
import { SERVICE_TRANSLATION_KEYS, type ServiceCode } from '@/constants/services'
import type { Package } from '@/types'

const API_BASE_URL = '/api/v1'
const ITEMS_PER_PAGE = 20
const MAIN_PURPOSE = 'photography'

const router = useRouter()
const cartStore = useCartStore()
const { t, locale } = useI18n()

// State
const packages = ref<Package[]>([])
const totalCount = ref(0)
const currentPage = ref(1)
const loading = ref(false)
const filters = ref({
  package_type: '',
  gender: ''
})

// Success Modal State
const showSuccessModal = ref(false)
const selectedPackageForConfirm = ref<Package | null>(null)

// Computed
const totalPages = computed(() => Math.ceil(totalCount.value / ITEMS_PER_PAGE))

const hasActiveFilters = computed(() => {
  return filters.value.package_type !== '' || filters.value.gender !== ''
})

const visiblePages = computed(() => {
  const pages: number[] = []
  const maxVisible = 5

  if (totalPages.value <= maxVisible) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i)
    }
  } else {
    let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages.value, start + maxVisible - 1)

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
  }

  return pages
})

// Fetch packages on mount
onMounted(() => {
  fetchPackages()
})

async function fetchPackages() {
  loading.value = true

  try {
    const params = new URLSearchParams()
    params.append('page', currentPage.value.toString())
    params.append('per_page', ITEMS_PER_PAGE.toString())
    params.append('main_purpose', MAIN_PURPOSE)

    if (filters.value.package_type === 'full_set') {
      params.append('full_set', 'true')
    } else if (filters.value.package_type) {
      params.append('package_type', filters.value.package_type)
    }
    if (filters.value.gender) {
      params.append('gender', filters.value.gender)
    }

    const response = await fetch(`${API_BASE_URL}/packages?${params}`, {
      credentials: 'include'
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch packages')
    }

    packages.value = data.packages
    totalCount.value = data.total || data.packages.length
  } catch (err) {
    console.error('Failed to fetch packages:', err)
    packages.value = []
    totalCount.value = 0
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  currentPage.value = 1
  fetchPackages()
}

function resetFilters() {
  filters.value.package_type = ''
  filters.value.gender = ''
  currentPage.value = 1
  fetchPackages()
}

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchPackages()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function getGenderLabel(value: string): string {
  const keys: Record<string, string> = {
    female: 'packages.filters.female',
    male: 'packages.filters.male'
  }
  return keys[value] ? t(keys[value]) : value
}

function getPackageTypeLabel(value: string): string {
  const keys: Record<string, string> = {
    premium: 'packages.filters.premium',
    basic: 'packages.filters.basic'
  }
  return keys[value] ? t(keys[value]) : value
}

function getAvatarPath(ageGroup: string, gender: string): string {
  const genderLetter = gender === 'female' ? 'f' : 'm'
  const age = ageGroup || 'adult'
  return `/icons/${age}-${genderLetter}.png`
}

function getLocalizedPackageName(pkg: Package): string {
  const currentLocale = locale.value
  switch (currentLocale) {
    case 'th':
      return pkg.name_th || pkg.name_en
    case 'zh':
      return pkg.name_zh || pkg.name_en
    default:
      return pkg.name_en
  }
}

function getLocalizedPackageDescription(pkg: Package): string | undefined {
  const currentLocale = locale.value
  switch (currentLocale) {
    case 'th':
      return pkg.description_th || pkg.description_en
    case 'zh':
      return pkg.description_zh || pkg.description_en
    default:
      return pkg.description_en
  }
}

function formatPrice(price: string): string {
  return parseFloat(price).toLocaleString()
}

function getAgeGroupLabel(ageGroup: string): string {
  const keys: Record<string, string> = {
    child: 'packages.age_group.child',
    adult: 'packages.age_group.adult',
    elderly: 'packages.age_group.elderly'
  }
  return keys[ageGroup] ? t(keys[ageGroup]) : ageGroup
}

// Success Modal Functions
async function openConfirmModal(pkg: Package) {
  selectedPackageForConfirm.value = pkg

  // Add to cart immediately
  if (!cartStore.addItem(pkg, '', undefined)) return

  // Show success modal
  showSuccessModal.value = true
}

function closeSuccessModal() {
  showSuccessModal.value = false
  selectedPackageForConfirm.value = null
}

function goToCart() {
  closeSuccessModal()
  router.push('/booking')
}

function getPackageTargetLabel(pkg: Package | null): string {
  if (!pkg) return ''
  const ageLabel = getAgeGroupLabel(pkg.age_group)
  const genderLabel = getGenderLabel(pkg.gender)
  return `${ageLabel} ${genderLabel}`
}

function getServiceName(service: string): string {
  const key = SERVICE_TRANSLATION_KEYS[service as ServiceCode]
  return key ? t(key) : service
}
</script>
